import { z } from "zod";

const Id = z.string().min(1).max(64);
const Timestamp = z.string();

/**
 * Ausrollen des Büros aus dem Studio (24.10.2026): Der Owner hält „Jetzt ausrollen“, der Server legt nur einen
 * Wunsch ab, ein Helfer auf dem Host prüft und rollt aus. `requested`: Wunsch liegt beim Helfer, `running`: der
 * Helfer baut, `done`: das Büro meldet den neuen Stand, `failed`: abgebrochen mit Grund.
 */
export const ROLLOUT_STATES = ["requested", "running", "done", "failed"] as const;
export const RolloutState = z.enum(ROLLOUT_STATES);
export type RolloutState = z.infer<typeof RolloutState>;

export const FullSha = z.string().regex(/^[0-9a-f]{40}$/, "Erwartet eine vollständige Commit-Kennung");

export const Rollout = z.object({
  id: Id,
  sha: FullSha,
  state: RolloutState,
  message: z.string(),
  createdAt: Timestamp,
  finishedAt: Timestamp.nullable(),
});
export type Rollout = z.infer<typeof Rollout>;

/** Was das Cockpit zum Ausrollen zeigt; `available: false` mit Grund, wenn das Büro sich nicht selbst ausrollt. */
export const RolloutView = z.object({
  available: z.boolean(),
  reason: z.string().nullable(),
  /** Laufender Stand des Büros (Build-Kennung). */
  runningBuild: z.string(),
  mainSha: FullSha.nullable(),
  /** Commits auf `main`, die das Büro noch nicht hat, neueste zuerst. */
  ahead: z.array(z.object({ sha: FullSha, title: z.string() })),
  aheadTotal: z.number().int().nonnegative(),
  /** Aufträge, die gemergt sind und erst mit dem Ausrollen ausgeliefert sind. */
  waitingOrders: z.array(z.object({ id: Id, title: z.string() })),
  ci: z.enum(["none", "pending", "success", "failure"]),
  ready: z.boolean(),
  blockers: z.array(z.string()),
  last: Rollout.nullable(),
});
export type RolloutView = z.infer<typeof RolloutView>;

export const RequestRolloutInput = z.strictObject({ sha: FullSha });
export type RequestRolloutInput = z.infer<typeof RequestRolloutInput>;

/** Helfer auf einem App-Server (`szyos-deployer`), wie das Studio ihn zeigt. */
export const DeployAgent = z.object({
  id: Id,
  name: z.string(),
  online: z.boolean(),
  lastSeenAt: Timestamp.nullable(),
  apps: z.array(z.object({ key: z.string(), head: z.string().nullable() })),
  revoked: z.boolean(),
});
export type DeployAgent = z.infer<typeof DeployAgent>;

/** Ausrollen einer App per Knopf (Projektseite); `configured: false`, solange kein Ziel eingestellt ist. */
export const ProjectRolloutView = z.object({
  configured: z.boolean(),
  agent: DeployAgent.nullable(),
  app: z.string().nullable(),
  /** Stand des Checkouts auf dem App-Server laut Helfer. */
  liveSha: FullSha.nullable(),
  mainSha: FullSha.nullable(),
  ahead: z.array(z.object({ sha: FullSha, title: z.string() })),
  aheadTotal: z.number().int().nonnegative(),
  ci: z.enum(["none", "pending", "success", "failure"]),
  ready: z.boolean(),
  blockers: z.array(z.string()),
  last: Rollout.extend({ log: z.string() }).nullable(),
});
export type ProjectRolloutView = z.infer<typeof ProjectRolloutView>;

export const SetDeployTargetInput = z.strictObject({
  agentId: Id.nullable(),
  app: z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]{0,39}$/, "App-Kennung aus der Liste des Helfers, z. B. radar")
    .nullable(),
});
export type SetDeployTargetInput = z.infer<typeof SetDeployTargetInput>;

export const DeployAgentRegisterInput = z.strictObject({
  code: z.string().min(10).max(64),
  name: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/, "Name aus Buchstaben, Ziffern, . _ -"),
});
export type DeployAgentRegisterInput = z.infer<typeof DeployAgentRegisterInput>;
